import Phaser from 'phaser';
import { PhysicsWorld, Vec2 } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

interface Level2Config {
  slopeAngle: number;
  slopeLength: number;
  targetVelocity: {
    min: number;
    max: number;
  };
  startPosition: { x: number; y: number };
  endPosition: { x: number; y: number };
}

const DEFAULT_LEVEL2_CONFIG: Level2Config = {
  slopeAngle: Math.PI / 6,
  slopeLength: 400,
  targetVelocity: {
    min: 8,
    max: 12,
  },
  startPosition: { x: 150, y: GAME_HEIGHT - 150 },
  endPosition: { x: 550, y: GAME_HEIGHT - 350 },
};

export class Level2Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private config: Level2Config;
  private velocityInput!: Phaser.GameObjects.DOMElement;
  private accelerationInput!: Phaser.GameObjects.DOMElement;
  private startButton!: Phaser.GameObjects.DOMElement;
  private velocityDisplay!: Phaser.GameObjects.Text;
  private isRunning: boolean = false;
  private elapsedTime: number = 0;
  private initialVelocity: number = 0;
  private acceleration: number = 0;
  private hasWon: boolean = false;

  constructor() {
    super({ key: 'Level2Scene' });
    this.config = DEFAULT_LEVEL2_CONFIG;
  }

  preload(): void {}

  create(): void {
    this.createPhysicsWorld();
    this.createSlope();
    this.createStickman();
    this.createGoalZone();
    this.createUI();
    this.createFormulaDisplay();
    this.createInstructions();
  }

  override update(_time: number, delta: number): void {
    if (this.isRunning) {
      this.elapsedTime += delta / 1000;
      this.physicsWorld.step(delta / 1000);

      if (this.acceleration !== 0) {
        const torso = this.stickman.getBody().getBody('torso');
        if (torso) {
          const forceX = this.acceleration * 50;
          torso.applyForceToCenter(Vec2(forceX, 0), true);
        }
      }

      this.updateVelocityDisplay();
      this.checkWinCondition();
      this.stickman.update();
    }
  }

  private createPhysicsWorld(): void {
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(0, -10),
      allowSleep: false,
    });

    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });
  }

  private createSlope(): void {
    const { slopeAngle, slopeLength, startPosition } = this.config;

    const slopeStartX = startPosition.x;
    const slopeStartY = startPosition.y;
    const slopeEndX = slopeStartX + slopeLength * Math.cos(slopeAngle);
    const slopeEndY = slopeStartY - slopeLength * Math.sin(slopeAngle);

    this.physicsWorld.createBody('slope', {
      x: (slopeStartX + slopeEndX) / 2,
      y: GAME_HEIGHT - (slopeStartY + slopeEndY) / 2,
      type: 'static',
      friction: 0.3,
    });

    const slopeGraphics = this.add.graphics();
    slopeGraphics.lineStyle(4, 0x4a4a6a);
    slopeGraphics.beginPath();
    slopeGraphics.moveTo(slopeStartX, slopeStartY);
    slopeGraphics.lineTo(slopeEndX, slopeEndY);
    slopeGraphics.strokePath();

    slopeGraphics.lineStyle(2, 0x3a3a5a);
    slopeGraphics.beginPath();
    slopeGraphics.moveTo(slopeStartX, slopeStartY);
    slopeGraphics.lineTo(slopeStartX, GAME_HEIGHT);
    slopeGraphics.moveTo(slopeEndX, slopeEndY);
    slopeGraphics.lineTo(slopeEndX, GAME_HEIGHT);
    slopeGraphics.strokePath();

    this.physicsWorld.createBody('ground', {
      x: GAME_WIDTH / 2,
      y: 20,
      type: 'static',
      friction: 0.5,
    });

    const groundGraphics = this.add.graphics();
    groundGraphics.lineStyle(4, 0x4a4a6a);
    groundGraphics.beginPath();
    groundGraphics.moveTo(0, GAME_HEIGHT - 20);
    groundGraphics.lineTo(GAME_WIDTH, GAME_HEIGHT - 20);
    groundGraphics.strokePath();
  }

  private createStickman(): void {
    const { startPosition } = this.config;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startPosition.x,
        y: startPosition.y,
        scale: 1,
        colors: {
          line: 0xffffff,
          headFill: 0x1a1a2e,
          lineWidth: 3,
        },
      },
      GAME_HEIGHT
    );
  }

  private createGoalZone(): void {
    const { endPosition, targetVelocity } = this.config;

    const goalWidth = 80;
    const goalHeight = 100;

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(0x4ade80, 0.3);
    goalGraphics.fillRect(
      endPosition.x - goalWidth / 2,
      endPosition.y - goalHeight,
      goalWidth,
      goalHeight
    );
    goalGraphics.lineStyle(2, 0x4ade80);
    goalGraphics.strokeRect(
      endPosition.x - goalWidth / 2,
      endPosition.y - goalHeight,
      goalWidth,
      goalHeight
    );

    this.add.text(
      endPosition.x - goalWidth / 2,
      endPosition.y - goalHeight - 20,
      `目标速度: ${targetVelocity.min}-${targetVelocity.max}`,
      {
        fontSize: '12px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    );
  }

  private createUI(): void {
    this.velocityInput = this.add.dom(150, 50).createFromHTML(`
      <div style="display: flex; align-items: center; gap: 10px;">
        <label style="color: #ffffff; font-size: 14px;">初速度 (v₀):</label>
        <input type="number" id="velocity-input" value="0" 
               style="width: 60px; padding: 5px; border: 1px solid #4a4a6a; 
                      background: #2a2a4a; color: #ffffff; font-size: 14px;">
        <span style="color: #888888; font-size: 12px;">单位/秒</span>
      </div>
    `);

    this.accelerationInput = this.add.dom(350, 50).createFromHTML(`
      <div style="display: flex; align-items: center; gap: 10px;">
        <label style="color: #ffffff; font-size: 14px;">加速度 (a):</label>
        <input type="number" id="acceleration-input" value="0" 
               style="width: 60px; padding: 5px; border: 1px solid #4a4a6a; 
                      background: #2a2a4a; color: #ffffff; font-size: 14px;">
        <span style="color: #888888; font-size: 12px;">单位/秒²</span>
      </div>
    `);

    this.startButton = this.add.dom(550, 50).createFromHTML(`
      <button id="start-btn" 
              style="padding: 10px 20px; background: #4ade80; color: #1a1a2e; 
                     border: none; border-radius: 4px; font-size: 14px; cursor: pointer;">
        开始运动
      </button>
    `);

    const btnElement = this.startButton.node.querySelector('#start-btn') as HTMLButtonElement;
    btnElement.addEventListener('click', () => this.startSimulation());

    const resetButton = this.add.dom(650, 50).createFromHTML(`
      <button id="reset-btn" 
              style="padding: 10px 20px; background: #ff6b6b; color: #ffffff; 
                     border: none; border-radius: 4px; font-size: 14px; cursor: pointer;">
        重置
      </button>
    `);

    const resetBtnElement = resetButton.node.querySelector('#reset-btn') as HTMLButtonElement;
    resetBtnElement.addEventListener('click', () => this.resetSimulation());

    this.velocityDisplay = this.add.text(150, 100, '当前速度: 0.00 单位/秒', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
  }

  private createFormulaDisplay(): void {
    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 80,
      '加速度公式: a = Δv / Δt',
      {
        fontSize: '20px',
        color: '#ffd700',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5, 0);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 55,
      '速度公式: v = v₀ + a × t',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5, 0);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 35,
      '时间 (t): 0.00 秒',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5, 0);
  }

  private createInstructions(): void {
    this.add.text(
      10,
      130,
      '【教学目标】理解加速度概念\n' +
      '加速度表示速度变化快慢：a = (终点速度 - 初速度) / 时间\n' +
      '提示：设置初速度和加速度，使火柴人到达终点时速度在目标范围内！',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        wordWrap: { width: 300 },
      }
    );
  }

  private startSimulation(): void {
    if (this.isRunning || this.hasWon) return;

    const velocityInputElement = this.velocityInput.node.querySelector('#velocity-input') as HTMLInputElement;
    const accelerationInputElement = this.accelerationInput.node.querySelector('#acceleration-input') as HTMLInputElement;

    this.initialVelocity = parseFloat(velocityInputElement.value) || 0;
    this.acceleration = parseFloat(accelerationInputElement.value) || 0;

    const torso = this.stickman.getBody().getBody('torso');
    if (torso) {
      torso.setLinearVelocity(Vec2(this.initialVelocity * 5, 0));
      torso.setAwake(true);
    }

    this.isRunning = true;
    this.elapsedTime = 0;

    const btnElement = this.startButton.node.querySelector('#start-btn') as HTMLButtonElement;
    btnElement.textContent = '运行中...';
    btnElement.style.background = '#888888';
    btnElement.disabled = true;
  }

  private resetSimulation(): void {
    this.isRunning = false;
    this.hasWon = false;
    this.elapsedTime = 0;
    this.initialVelocity = 0;
    this.acceleration = 0;

    this.stickman.destroy();
    this.createStickman();

    this.velocityDisplay.setText('当前速度: 0.00 单位/秒');

    const btnElement = this.startButton.node.querySelector('#start-btn') as HTMLButtonElement;
    btnElement.textContent = '开始运动';
    btnElement.style.background = '#4ade80';
    btnElement.disabled = false;

    const velocityInputElement = this.velocityInput.node.querySelector('#velocity-input') as HTMLInputElement;
    const accelerationInputElement = this.accelerationInput.node.querySelector('#acceleration-input') as HTMLInputElement;
    velocityInputElement.value = '0';
    accelerationInputElement.value = '0';

    const resultText = this.children.getByName('resultText') as Phaser.GameObjects.Text;
    if (resultText) {
      resultText.destroy();
    }
  }

  private updateVelocityDisplay(): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (torso) {
      const velocity = torso.getLinearVelocity();
      const currentVelocity = Math.abs(velocity.x) / 5;
      this.velocityDisplay.setText(`当前速度: ${currentVelocity.toFixed(2)} 单位/秒`);
    }

    const timeText = this.children.list.find(
      (child) => child instanceof Phaser.GameObjects.Text && 
      (child as Phaser.GameObjects.Text).text.includes('时间')
    ) as Phaser.GameObjects.Text;
    if (timeText) {
      timeText.setText(`时间 (t): ${this.elapsedTime.toFixed(2)} 秒`);
    }
  }

  private checkWinCondition(): void {
    if (this.hasWon) return;

    const { endPosition, targetVelocity } = this.config;
    const stickmanPos = this.stickman.getPosition();

    const distanceToEnd = Math.abs(stickmanPos.x - endPosition.x);
    const verticalDistance = Math.abs(stickmanPos.y - endPosition.y);

    if (distanceToEnd < 50 && verticalDistance < 100) {
      const torso = this.stickman.getBody().getBody('torso');
      if (torso) {
        const velocity = torso.getLinearVelocity();
        const currentVelocity = Math.abs(velocity.x) / 5;

        if (currentVelocity >= targetVelocity.min && currentVelocity <= targetVelocity.max) {
          this.onWin();
        } else {
          this.onLose(currentVelocity);
        }
      }
    }
  }

  private onWin(): void {
    this.hasWon = true;
    this.isRunning = false;

    const gameState = getGameState();
    gameState.completeLevel(2, this.elapsedTime);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      '🎉 恭喜！你理解了加速度概念！',
      {
        fontSize: '24px',
        color: '#4ade80',
        fontFamily: 'Arial',
        align: 'center',
      }
    )
      .setOrigin(0.5, 0.5)
      .setName('resultText');

    const expectedVelocity = this.initialVelocity + this.acceleration * this.elapsedTime;
    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 40,
      `验证: v = ${this.initialVelocity} + ${this.acceleration} × ${this.elapsedTime.toFixed(2)} ≈ ${expectedVelocity.toFixed(2)}`,
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    )
      .setOrigin(0.5, 0.5);

    this.createLevel2CompletionButtons();
  }

  private createLevel2CompletionButtons(): void {
    const gameState = getGameState();

    if (gameState.isLevelUnlocked(3)) {
      const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 80, '下一关', {
        fontSize: '18px',
        color: '#4ade80',
        fontFamily: 'Arial',
        backgroundColor: '#333333',
        padding: { x: 15, y: 8 },
      }).setOrigin(0.5).setInteractive();

      nextBtn.on('pointerdown', () => {
        this.scene.start('Level3Scene');
      });
    }

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 80, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  private onLose(currentVelocity: number): void {
    this.isRunning = false;

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      `速度未达标！当前速度: ${currentVelocity.toFixed(2)}\n目标速度: ${this.config.targetVelocity.min}-${this.config.targetVelocity.max}`,
      {
        fontSize: '18px',
        color: '#ff6b6b',
        fontFamily: 'Arial',
        align: 'center',
      }
    )
      .setOrigin(0.5, 0.5)
      .setName('resultText');

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 60,
      '请调整初速度或加速度，点击重置重新尝试',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    )
      .setOrigin(0.5, 0.5);
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getGameState(): {
    isRunning: boolean;
    elapsedTime: number;
    hasWon: boolean;
    initialVelocity: number;
    acceleration: number;
  } {
    return {
      isRunning: this.isRunning,
      elapsedTime: this.elapsedTime,
      hasWon: this.hasWon,
      initialVelocity: this.initialVelocity,
      acceleration: this.acceleration,
    };
  }
}